import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiKeysApi, webhooksApi } from '@/api/integrations.api';

export const integrationKeys = {
  keys: ['api-keys'] as const,
  hooks: ['webhooks'] as const,
};

// ---------- API keys ----------
export function useApiKeys() {
  return useQuery({ queryKey: integrationKeys.keys, queryFn: () => apiKeysApi.list() });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; scopes: string[] }) => apiKeysApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.keys }),
    onError: (err: Error) => toast.error(err.message || 'Error al crear la key'),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      toast.success('Key revocada');
      qc.invalidateQueries({ queryKey: integrationKeys.keys });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al revocar'),
  });
}

// ---------- Webhooks ----------
export function useWebhooks() {
  return useQuery({ queryKey: integrationKeys.hooks, queryFn: () => webhooksApi.list() });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { url: string; events: string[] }) => webhooksApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: integrationKeys.hooks }),
    onError: (err: Error) => toast.error(err.message || 'Error al crear el webhook'),
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => webhooksApi.remove(id),
    onSuccess: () => {
      toast.success('Webhook eliminado');
      qc.invalidateQueries({ queryKey: integrationKeys.hooks });
    },
    onError: (err: Error) => toast.error(err.message || 'Error al eliminar'),
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) => webhooksApi.test(id),
    onSuccess: () => toast.success('Evento test.ping encolado — mirá tu endpoint'),
    onError: (err: Error) => toast.error(err.message || 'Error al enviar el test'),
  });
}
